import { useEffect, useState, useCallback, useRef, createContext } from "react";
import { supabase } from "./supabase";

export const FINISH_TAG = 'L01';
export const loops = Array.from({ length: 7 }, (_, i) => `L0${i + 1}`);

export const loopDistances = {
    L01: 0,
    L02: 35,
    L03: 50,
    L04: 107,
    L05: 150,
    L06: 160,
    L07: 232
}

const dictDiff = (dict1, dict2) => {
    const changed = {};
    for (const key in dict1) {
        if (dict1[key] !== dict2[key]) {
            changed[key] = [dict1[key], dict2[key]];
        }
    }
    return changed;
};

const MAX_LAPTIME = 60;

const SprintContext = createContext();

const SprintProvider = ({ venueId, currentSession, children }) => {

    const [laps, setLaps] = useState([]);
    const [lapBuilder, setLapBuilder] = useState({});
    const [fastestLap, setFastestLap] = useState(null);
    const [lastUpdate, setLastUpdate] = useState({});
    const [targetRider, setTargetRider] = useState({});
    const [fastest200mAllTime, setFastest200mAllTime] = useState(null);
    const [tags, setTags] = useState([]);

    const lastUpdateRef = useRef(lastUpdate);
    const lapBuilderRef = useRef(lapBuilder);

    const postgresInsertedLapCallback = (payload) => {
        setLaps((prev) => [...prev, { ...payload.new, start: new Date(payload.new.start).getTime() }]);
    }

    const postgresUpdatedLapCallback = (payload) => {
        const { new: record } = payload;
        setLaps((prev) => {
            const index = prev.findIndex((lap) => lap.tag === record.tag && lap.start === new Date(record.start).getTime());
            if (index !== -1) {
                const updatedLap = { ...prev[index], ...record };
                return [...prev.slice(0, index), updatedLap, ...prev.slice(index + 1)];
            }
            return prev;
        });
    }

    const postgresDeletedLapCallback = (payload) => {
        const { old: record } = payload;
        setLaps((prev) => {
            const index = prev.findIndex((lap) => lap.tag === record.tag && lap.start === new Date(record.start).getTime());
            if (index !== -1) {
                return [...prev.slice(0, index), ...prev.slice(index + 1)];
            }
            return prev;
        });
    }

    const postgresUpdatedTargetsCallback = (payload) => {
        const { target_tag: targetTag } = payload.new;
        let tag = null;
        if (targetTag !== null) {
            tag = targetTag.includes(',') ? targetTag.split(',')[0] : targetTag;
        }

        setTargetRider(tag);
    }

    const postgresChangesCallback = useCallback((payload) => {
        const { new: record } = payload;
        const { tag: tagname, location: updatedLoop } = record;

        //if (tagname !== 'SK16113') return;
        //if (!tagname.startsWith('S')) return;

        const updatedRtcTime = record[`${updatedLoop}.rtcTime`];

        if (tagname in lastUpdateRef.current) {
            const diff = dictDiff(lastUpdateRef.current[tagname], record);
            if ('location' in diff) {
                console.log(`[${tagname}] Changed location from ${diff.location[0]} to ${diff.location[1]}`);
            }

            if (updatedLoop === FINISH_TAG) {
                setLapBuilder((prev) => ({
                    ...prev,
                    [tagname]: {
                        start: updatedRtcTime
                    }
                }));
            } else {
                setLapBuilder((prev) => ({
                    ...prev,
                    [tagname]: {
                        ...prev[tagname],
                        [updatedLoop]: updatedRtcTime,
                    },
                }));
            }
        } else if (updatedLoop === FINISH_TAG) {
            setLapBuilder((prev) => ({
                ...prev,
                [tagname]: {
                    start: updatedRtcTime,
                    [updatedLoop]: updatedRtcTime,
                },
            }));
        }

        setLastUpdate({
            ...lastUpdateRef.current,
            [tagname]: record,
        });
    }, []);

    const findTagMapping = (tag) => {
        const mapping = tags.find((t) => t.tag === tag);
        return mapping ? mapping.name : tag;
    }

    useEffect(() => {
        const taskListenerLapUpdates = supabase.channel('laptimes').on(
            "postgres_changes",
            { event: "*", schema: "public", table: "laptimes" },
            (payload) => {
                if (payload.eventType === "INSERT" && payload.new.venue_id === venueId) {
                    postgresChangesCallback(payload)
                }
            },
        ).subscribe();


        const taskListenerLaps = supabase.channel('laps').on(
            "postgres_changes",
            { event: "*", schema: "public", table: "laps" },
            (payload) => {
                if (payload.new.venue_id === parseInt(venueId)) {
                    if (payload.eventType === "INSERT") {
                        postgresInsertedLapCallback(payload);
                    } else if (payload.eventType === "UPDATE") {
                        postgresUpdatedLapCallback(payload);
                    } else if (payload.eventType === "DELETE") {
                        postgresDeletedLapCallback(payload);
                    }
                }
            },
        ).subscribe();

        // Fetch initial laps and set them in the laps state
        const fetchInitialLaps = async () => {
            console.log("currentSession", currentSession);
            //currentSession.start_time
            //currentSession.end_time

            if (venueId) {
                if (currentSession !== null) {
                    const { data, error } = await supabase
                        .from("laps")
                        .select("*")
                        .eq("venue_id", venueId)
                        .gte("start", currentSession.start_time)
                        .lte("start", currentSession.end_time)
                    if (error) {
                        console.error("Error fetching initial laps:", error);
                    } else {
                        const mappedData = data.map((lap) => ({
                            ...lap,
                            start: new Date(lap.start).getTime(),
                        }));
                        setLaps(mappedData || []);
                    }
                }
                else{
                    const { data, error } = await supabase
                        .from("laps")
                        .select("*")
                        .eq("venue_id", venueId)
                        .gte("start", new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString());
                    if (error) {
                        console.error("Error fetching initial laps:", error);
                    } else {
                        const mappedData = data.map((lap) => ({
                            ...lap,
                            start: new Date(lap.start).getTime(),
                        }));
                        setLaps(mappedData || []);
                    }
                }

                // fetch the fastest result of all time for 200m sprint
                // const { data: fastestLapAllTimeData, error: fastestLapAllTimeError } = await supabase
                //     .from("laps")
                //     .select("*")
                //     .eq("venue_id", venueId)
                //     .not("L01", "is", null)
                //     .not("L03", "is", null)
                //     .order("start", { ascending: true });
                // console.log("fastestLapAllTimeData", fastestLapAllTimeData)
                // if (fastestLapAllTimeError) {
                //     console.error("Error fetching fastest lap of all time:", fastestLapAllTimeError);
                // } else if (fastestLapAllTimeData && fastestLapAllTimeData.length > 0) {
                //     // remove records where L01 is not smallar than L03
                //     const filteredFastestLapAllTimeData = fastestLapAllTimeData.filter(lap => lap.L01 > lap.L03 && lap.L05 > lap.L03);
                //     const fastestInterval = filteredFastestLapAllTimeData.map(lap => ({
                //         ...lap,
                //         intervalTime: lap.L01 - lap.L03
                //     })).reduce((prev, curr) => (prev.intervalTime < curr.intervalTime ? prev : curr));
                //     console.log("Fastest interval time:", fastestInterval.tag, fastestInterval.intervalTime.toFixed(3),fastestInterval);

                //     setFastest200mAllTime(
                //         {
                //             "men": {
                //                 "tag": fastestInterval.tag,
                //                 "name": findTagMapping(fastestInterval.tag),
                //                 "time": fastestInterval.intervalTime.toFixed(3)
                //             }
                //         }
                //     )
                // }

            }
        };

        fetchInitialLaps();

        const taskListenerTargets = supabase.channel('screen_configuration').on(
            "postgres_changes",
            { event: "*", schema: "public", table: "screen_configuration", filter: 'id=eq.' + venueId },
            (payload) => postgresUpdatedTargetsCallback(payload),
        )

        return () => {
            taskListenerLapUpdates.unsubscribe();
            taskListenerLaps.unsubscribe();
            taskListenerTargets.unsubscribe();
        }
    }, [postgresChangesCallback, venueId, currentSession]);

    useEffect(() => {
        lastUpdateRef.current = lastUpdate;
    }, [lastUpdate])

    useEffect(() => {
        lapBuilderRef.current = lapBuilder;
    }, [lapBuilder])

    useEffect(() => {
        if (laps.length === 0) return;

        const newLap = laps[laps.length - 1];

        // Ignore start signal
        if (newLap.tag === '9992') return;
        if (fastestLap === null || (newLap && laps[fastestLap] && FINISH_TAG in newLap && FINISH_TAG in laps[fastestLap] && newLap[FINISH_TAG] < laps[fastestLap][FINISH_TAG])) {
            setFastestLap(laps.length - 1);
        }
    }, [laps.length, fastestLap])

    useEffect(() => {
        const fetchTags = async () => {
            const { data } = await supabase.from("tags").select("*");
            setTags(data || []);
        };

        const fetchLaps = async () => {
            // determine fastest lap from database
            let flap = null;
            for (let i = 0; i < laps.length; i++) {
                const lap = laps[i];
                //if(flap !== null)
                //    console.log(lap[FINISH_TAG], mappedData[flap][FINISH_TAG])
                if (flap === null || lap[FINISH_TAG] < laps[flap][FINISH_TAG]) {
                    flap = i;
                }

            }

            setFastestLap(flap);
        }

        fetchTags();
        fetchLaps();
    }, [laps]);

    return <SprintContext.Provider value={{ laps, fastestLap, lapBuilder, targetRider, findTagMapping, fastest200mAllTime }}>{children}</SprintContext.Provider>;
}

export { SprintProvider, SprintContext };